import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import Certification from "../components/Certification";
import Seminars from "../components/Seminars";
import { useDispatch, useSelector } from "react-redux";
import { getNoTopics } from "../../../store/topicSlice";
import { useTranslation } from 'react-i18next';
import { useTranslationService } from '../../../utils/translate';

const ButtonAcc = styled.button`
    @media(max-width: 1032px) {
        display: block;
        flex-direction: column;
    }
`;

const CourseInfo = () => {
    const { t, i18n } = useTranslation();
    const { translateText } = useTranslationService();
    const [activeTab, setActiveTab] = useState("The Course");
    const dispatch = useDispatch();
    const [checked, setChecked] = useState('');
    const [openAccordion, setOpenAccordion] = useState(null);
    const { items, loading, error, topicNo } = useSelector((state) => state.topics);
    const [translatedTopics, setTranslatedTopics] = useState([]);

    const toggleAccordion = (index) => {
        setOpenAccordion(openAccordion === index ? null : index);
    };

    useEffect(() => {
        dispatch(getNoTopics());
    }, [dispatch]);

    useEffect(() => {
        if (items?.length > 0 && items[0]?.lectures?.length > 0) {
            setChecked(items[0]?.lectures[0]?.video);
        }
    }, [items]);

    useEffect(() => {
        const translateTopics = async () => {
            if (i18n.language === 'en') {
                setTranslatedTopics(topicNo);
                return;
            }
            
            const translated = await Promise.all(
                topicNo.map(async (topic) => {
                    try {
                        const translatedTitle = await translateText(topic.title);
                        return { ...topic, translatedTitle };
                    } catch (error) {
                        console.error('Translation error:', error);
                        return { ...topic, translatedTitle: topic.title };
                    }
                })
            );
            setTranslatedTopics(translated);
        };

        if (topicNo.length > 0) {
            translateTopics();
        }
    }, [topicNo, i18n.language]);

    const tabs = {
        "The Course": t('course_info.tabs.course'),
        "Live Sessions": t('course_info.tabs.sessions'),
        "Certificate": t('course_info.tabs.certificate')
    };

    const content = {
        "The Course": {
            title: "",
            details: "",
            items: [
                t('course_info.topics.topic1'),
                t('course_info.topics.topic2'),
                t('course_info.topics.topic3')
            ]
        },
        "Live Sessions": {
            title: t('course_info.sessions.title'),
            details: t('course_info.sessions.description'),
        },
        "Certificate": {
            title: t('course_info.certificate.title'),
            details: t('course_info.certificate.description'),
            image: "/images/CourseInfo/cert.png"
        }
    };

    if (loading) return <p>{t('common.loading')}</p>;
    if (error) return <p>{t('errors.loading_error')}: {error}</p>;

    return (
        <div className="flex flex-col items-center p-0 sm:pt-4 pt-4">
            <div className="flex w-full space-x-4 mb-4">
                <Swiper
                    spaceBetween={10}
                    slidesPerView={3}
                    breakpoints={{
                        320: { slidesPerView: 2.5 },
                        768: { slidesPerView: 3 },
                    }}
                    className="w-full"
                >
                    {Object.keys(tabs).map((tabKey) => (
                        <SwiperSlide key={tabKey}>
                            <button
                                onClick={() => {
                                    setActiveTab(tabKey);
                                    setOpenAccordion(null);
                                }}
                                className={`w-full px-4 py-2 rounded-xl transition-all ${activeTab === tabKey ? "bg-[#8C1C1C] text-white" : "bg-[#F0F0F0]"
                                    }`}
                            >
                                {tabs[tabKey]}
                            </button>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>

            <div className="w-full">
                <h2 className="text-[28px] font-bold">{content[activeTab].title}</h2>
                <p className="text-gray-600 mt-3 mb-2 text-[16px]">{content[activeTab].details}</p>
                {activeTab === "The Course" ? (
                    translatedTopics.map((item, index) => (
                        <div key={index}>
                            <ButtonAcc
                                onClick={() => toggleAccordion(index)}
                                style={{ border: '1px solid #e1e1e1' }}
                                className={` flex flex-col text-sm py-4 px-4 text-left bg-[#f7f7f7] focus:outline-none justify-between w-full rounded-none sm:rounded-none`}
                            >
                                <div className="flex items-center justify-between w-full">
                                    <div className='flex gap-[10px]'>
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
                                             xmlns="http://www.w3.org/2000/svg">
                                            <path d="M6 10V8C6 4.69 7 2 12 2C17 2 18 4.69 18 8V10" stroke="#292D32"
                                                  strokeWidth="1.5" strokeLinecap="round"
                                                  strokeLinejoin="round"/>
                                            <path
                                                d="M12 18.5C13.3807 18.5 14.5 17.3807 14.5 16C14.5 14.6193 13.3807 13.5 12 13.5C10.6193 13.5 9.5 14.6193 9.5 16C9.5 17.3807 10.6193 18.5 12 18.5Z"
                                                stroke="#292D32" strokeWidth="1.5" strokeLinecap="round"
                                                strokeLinejoin="round"/>
                                            <path
                                                d="M17 22H7C3 22 2 21 2 17V15C2 11 3 10 7 10H17C21 10 22 11 22 15V17C22 21 21 22 17 22Z"
                                                stroke="#292D32" strokeWidth="1.5" strokeLinecap="round"
                                                strokeLinejoin="round"/>
                                        </svg>
                                        <div>
                                            <p className="font-medium">
                                                {item.translatedTitle || item.title}
                                            </p>
                                            <div className='flex items-center gap-[6px] w-full'>
                                                <span>
                                                    <svg width="3" height="4" viewBox="0 0 3 4"
                                                         fill="none"
                                                         xmlns="http://www.w3.org/2000/svg">
                                                        <circle opacity="0.7" cx="1.5" cy="2" r="1.5" fill="#1E1E1E" fillOpacity="0.3"/>
                                                    </svg>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </ButtonAcc>
                        </div>
                    ))
                ) : activeTab === "Live Sessions" ? (
                    <>
                      <Seminars />
                    </>
                ) : (
                    <></>
                    // <Certification content={content} />
                )}
            </div>
        </div>
    );
};

export default CourseInfo;